import React from 'react';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ForwardedHTMLProps } from 'src/tef/components/utils/types';

import { useCardContext } from './CardContext';

interface CardBodyProps extends ForwardedHTMLProps {
  children: React.ReactNode;
  className?: string;
  disableMargins?: boolean;
}

export const CardBody = React.forwardRef<HTMLDivElement, CardBodyProps>(
  ({ children, className, disableMargins = false, ...props }, ref) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);
    const cardContext = useCardContext();

    const hasHeader = cardContext?.hasHeader ?? false;
    const spacing = cardContext?.spacing || 'default';

    const spacingMap = {
      none: {
        margin: '',
        topMargin: '',
      },
      compact: {
        margin: '-mx-3 -mb-3 px-3',
        topMargin: '-mt-3',
      },
      default: {
        margin: '-mx-5 -mb-5 px-5',
        topMargin: '-mt-5',
      },
      spacious: {
        margin: '-mx-8 -mb-8 px-8',
        topMargin: '-mt-8',
      },
    };

    const spacingClasses = spacingMap[spacing];

    return (
      <div
        ref={ref}
        className={cn(
          !disableMargins && spacingClasses.margin,
          !disableMargins && !hasHeader && spacingClasses.topMargin,
          'py-4',
          tef.colors.foreground,
          className
        )}
        {...forwardedProps}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardBody.displayName = 'CardBody';
